// Native-shell bridge (Capacitor iOS) — window.AtlasNative.
//
// The iOS app (atlas-mobile/) is a thin shell that loads this same SPA from
// the production URL (Capacitor server.url), so this file is the only place
// the web code talks to native APIs. Plugins are reached through
// window.Capacitor.Plugins.<Name>, which the shell injects at document start
// — no @capacitor/* JS is bundled here.
//
// Everything is inert in a browser: isNative is false, plugin() returns null,
// no listeners or shims are installed, and the helpers fall back to today's
// web behaviour (window.open / <a download>).
//
//   AtlasNative.isNative / .platform     'ios' | 'web'
//   AtlasNative.plugin(name)             Capacitor plugin proxy or null
//   AtlasNative.openExternal(url, opts)  in-app Safari sheet (SFSafariViewController);
//                                        opts.system or a known app host → hand to iOS
//   AtlasNative.saveFile(blob, name, mime)  Cache dir + Share sheet ("Save to Files",
//                                        AirDrop, Mail…); web → normal download
//   AtlasNative.haptic(style)            light|medium|heavy|selection|success|warning|error
//   AtlasNative.onResume(fn)             app back in foreground → fn(); returns unsubscribe
//
// Installed only in the shell:
//   • App 'resume' → refresh the auth token, dispatch 'atlas-data-changed'.
//   • Deep links (appUrlOpen / launch URL): com.industrypro.atlas://open#/…
//     and https://<atlas host>/#/… → window.location.hash.
//   • A capture-phase click handler routing external / target=_blank links
//     through openExternal (WKWebView has no tabs or popups).
//   • A window.open shim doing the same for script-opened URLs.
//   • Status bar text colour follows <html data-theme>.

(function () {
  const isNative = (() => {
    try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); }
    catch (_) { return false; }
  })();
  const platform = isNative && window.Capacitor.getPlatform ? window.Capacitor.getPlatform() : 'web';

  const plugin = (name) => {
    if (!isNative) return null;
    const all = window.Capacitor && window.Capacitor.Plugins;
    return (all && all[name]) || null;
  };

  // Captured before the shim below replaces it.
  const nativeWindowOpen = window.open ? window.open.bind(window) : null;

  // Hosts whose links work better handed to iOS than shown in the in-app
  // Safari sheet: meeting links open the Teams/Zoom app via universal links,
  // and Microsoft 365 pages need the user's Safari sign-in (the in-app sheet
  // does not share Safari's cookies).
  const SYSTEM_HOST_RE = /(^|\.)(teams\.microsoft\.com|teams\.live\.com|zoom\.us|meet\.google\.com|webex\.com|office\.com|office365\.com|sharepoint\.com|onedrive\.live\.com|live\.com|microsoft\.com|linkedin\.com)$/i;

  const parseUrl = (url) => {
    try { return new URL(String(url), window.location.href); } catch (_) { return null; }
  };
  const isHttp = (u) => !!u && (u.protocol === 'http:' || u.protocol === 'https:');

  // A top-level navigation the shell doesn't own is cancelled by Capacitor
  // and passed to UIApplication.open — iOS then picks the app (Teams,
  // Outlook, Phone, Mail) or Safari. The page itself stays put. Only
  // schemes that hand off to another app: a javascript:/file: URL from
  // server data must never run inside the page that holds the session and
  // the native bridge.
  const HANDOFF_SCHEME_RE = /^(mailto|tel|sms|facetime|facetime-audio|maps|msteams|zoomus|zoommtg|webex|ms-outlook|ms-word|ms-excel|ms-powerpoint|onenote):$/i;
  const handToSystem = (href) => {
    const u = parseUrl(href);
    if (u && HANDOFF_SCHEME_RE.test(u.protocol)) window.location.href = u.href;
    else if (u && isHttp(u)) window.location.href = u.href;
  };

  const openExternal = (url, opts) => {
    const u = parseUrl(url);
    if (!u) return Promise.resolve();
    if (!isNative) {
      // tel:/mailto:/sms: navigate in place (iOS hands them to Phone/Mail) —
      // window.open would leave a blank tab behind in mobile Safari.
      if (HANDOFF_SCHEME_RE.test(u.protocol)) window.location.href = u.href;
      else if (isHttp(u) && nativeWindowOpen) nativeWindowOpen(u.href, '_blank', 'noopener');
      return Promise.resolve();
    }
    if (!isHttp(u)) { handToSystem(u.href); return Promise.resolve(); }   // mailto:, tel:, …
    const browser = plugin('Browser');
    if ((opts && opts.system) || SYSTEM_HOST_RE.test(u.hostname) || !browser) {
      handToSystem(u.href);
      return Promise.resolve();
    }
    return browser.open({ url: u.href }).catch(() => handToSystem(u.href));
  };

  // Plain <a download> — the browser path, also the fallback when a shell
  // build lacks the Filesystem/Share plugins. (Deliberately not
  // window.downloadBlob: that one delegates here when native.)
  const anchorDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || '').replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error || new Error('Could not read file'));
    r.readAsDataURL(blob);
  });

  // WKWebView ignores <a download> and blob: navigations, so a native save is
  // "write to the app's cache, then open the share sheet". Resolves
  // { uri } (native) or {} (web); a dismissed share sheet is not an error.
  const saveFile = async (blob, filename, mime) => {
    const name = String(filename || 'download').replace(/[\/\\:*?"<>|\u0000-\u001f]+/g, '_').slice(0, 180) || 'download';
    const fs = plugin('Filesystem');
    const share = plugin('Share');
    if (!isNative || !fs || !share) {
      anchorDownload(mime && blob && !blob.type ? new Blob([blob], { type: mime }) : blob, name);
      return {};
    }
    const data = await blobToBase64(blob);
    // One folder per save so two exports with the same name can't collide
    // while a share sheet still holds the first.
    const path = `atlas-files/${Date.now().toString(36)}/${name}`;
    const { uri } = await fs.writeFile({ path, data, directory: 'CACHE', recursive: true });
    try {
      await share.share({ title: name, files: [uri] });
    } catch (e) {
      const msg = String((e && e.message) || e || '');
      if (!/cancel/i.test(msg)) throw e;
    }
    return { uri };
  };

  const haptic = (style) => {
    const h = plugin('Haptics');
    if (!h) return;
    const s = String(style || 'light').toLowerCase();
    let p;
    if (s === 'selection') p = h.selectionChanged();
    else if (s === 'success' || s === 'warning' || s === 'error') p = h.notification({ type: s.toUpperCase() });
    else p = h.impact({ style: s === 'heavy' ? 'HEAVY' : s === 'medium' ? 'MEDIUM' : 'LIGHT' });
    if (p && p.catch) p.catch(() => {});
  };

  const resumeFns = new Set();
  const onResume = (fn) => {
    if (typeof fn !== 'function') return () => {};
    resumeFns.add(fn);
    return () => { resumeFns.delete(fn); };
  };

  window.AtlasNative = { isNative, platform, plugin, openExternal, saveFile, haptic, onResume };

  if (!isNative) return;

  // ── Resume: refresh the token first (an hour in the background expires the
  // access token, and the first requests would otherwise 401 then retry),
  // then tell data views to re-pull.
  const App = plugin('App');
  if (App && App.addListener) {
    App.addListener('resume', () => {
      const auth = window.AtlasAuth;
      const ready = auth && auth.getValidSession ? auth.getValidSession().catch(() => null) : Promise.resolve();
      ready.then(() => {
        window.dispatchEvent(new CustomEvent('atlas-data-changed', { detail: { source: 'resume' } }));
        resumeFns.forEach((fn) => { try { fn(); } catch (_) {} });
      });
    });

    // ── Deep links. Only hash routes are honoured, and the sign-in callback
    // is ignored here on purpose: ASWebAuthenticationSession consumes it, and
    // adopting tokens from an arbitrary app-opened URL would let another app
    // sign this one into a different account.
    const openDeepLink = (raw) => {
      const u = parseUrl(raw);
      if (!u) return;
      let hash = '';
      if (u.protocol === 'com.industrypro.atlas:') {
        if (u.host !== 'open') return;
        hash = u.hash;
      } else if (isHttp(u) && (u.host === window.location.host || u.hostname === 'ipro-atlas.onrender.com')) {
        hash = u.hash;
      }
      if (hash && hash.startsWith('#/') && hash !== window.location.hash) window.location.hash = hash;
    };
    App.addListener('appUrlOpen', (ev) => openDeepLink(ev && ev.url));
    // Cold start from a link: the event fired before this page loaded.
    // getLaunchUrl keeps returning the LAST link for the life of the app, so
    // remember the one already followed — otherwise every later page reload
    // (scope switch, layout switch, iOS reviving the web process) would jump
    // back to it.
    if (App.getLaunchUrl) {
      App.getLaunchUrl().then((r) => {
        if (!r || !r.url) return;
        let seen = null;
        try { seen = sessionStorage.getItem('atlas.launchUrl'); } catch (_) {}
        if (seen === r.url) return;
        try { sessionStorage.setItem('atlas.launchUrl', r.url); } catch (_) {}
        openDeepLink(r.url);
      }).catch(() => {});
    }
  }

  // ── Links. Capture phase so a row's onClick stopPropagation (common on
  // website/LinkedIn links inside clickable rows) can't keep this from
  // running. That also means it runs BEFORE React's handlers (React listens
  // at the root), so an <a> must not open its URL itself as well — give it
  // a plain href and let this route it. tel:/mailto:/sms: are left alone —
  // WKWebView hands them to iOS.
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button > 0) return;
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    const u = parseUrl(a.href);
    if (!isHttp(u)) return;
    const blank = (a.getAttribute('target') || '').toLowerCase() === '_blank';
    if (u.origin !== window.location.origin) {
      e.preventDefault();
      openExternal(u.href);
    } else if (blank) {
      // No tabs in the shell — open in place.
      e.preventDefault();
      if (u.pathname === window.location.pathname && u.search === window.location.search) window.location.hash = u.hash;
      else window.location.assign(u.href);
    }
  }, true);

  // ── window.open: same routing for script-opened URLs (Join meeting, Open
  // in Outlook, ⌘-click "new tab"). Returns null like a blocked popup; no
  // caller relies on the returned window except openAttachment, which has
  // its own native branch.
  window.open = function (url, target, features) {
    const u = url ? parseUrl(url) : null;
    if (!u || u.href === 'about:blank') return null;
    if (isHttp(u)) {
      if (u.origin !== window.location.origin) { openExternal(u.href); return null; }
      if (u.pathname === window.location.pathname && u.search === window.location.search) window.location.hash = u.hash;
      else window.location.assign(u.href);
      return null;
    }
    if (u.protocol === 'blob:' || u.protocol === 'data:') {
      return nativeWindowOpen ? nativeWindowOpen(url, target, features) : null;
    }
    handToSystem(u.href);      // mailto:, tel:, msteams:, …
    return null;
  };

  // ── Status bar text follows the app theme (the page draws under it).
  const StatusBar = plugin('StatusBar');
  if (StatusBar && StatusBar.setStyle) {
    const syncStatusBar = () => {
      const dark = document.documentElement.getAttribute('data-theme') === 'dark';
      // 'DARK' = light text for a dark background.
      StatusBar.setStyle({ style: dark ? 'DARK' : 'LIGHT' }).catch(() => {});
    };
    syncStatusBar();
    try {
      new MutationObserver(syncStatusBar).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    } catch (_) {}
  }
})();
